/**
 * @file CoreTransport - Lifecycle base for one wire protocol's transport
 * surface.
 *
 * A transport owns one protocol's I/O machinery — today HTTP (over `fetch`
 * for clients / Node's `http` module for servers), WebSocket (over `ws`),
 * the in-process `VirtualHostTransport` used by the integration harness;
 * tomorrow HTTP/2, HTTP/3, gRPC. Sits as a sibling to `CoreClient`,
 * `CoreService`, `CoreProvider` under the shared `CoreLifecycle` so the
 * Daemon's start/stop walk treats it uniformly.
 *
 * The base intentionally adds nothing on top of `CoreLifecycle` — no
 * abstract methods, no required hook surface. Subclasses (`HttpClientTransport`,
 * `WsClientTransport`, `VirtualHostTransport`, future server-side
 * transports) declare their own protocol verbs (`request`, `stream`,
 * `subscribe`, …). The split exists for type-system reasons: `CoreClient`
 * generic-constrains its `TTransports` record to `Record<string, CoreTransport>`,
 * which keeps "you can put a Postgres pool here" out of the type.
 *
 * @module
 */

import { CoreLifecycle } from './core_lifecycle.ts'

/**
 * Lifecycle-managed base for wire-protocol transports.
 *
 * @typeParam TProps - Constructor props; must include an optional `id`.
 */
export abstract class CoreTransport<
  TProps extends { id?: string } = { id?: string },
  TEvents extends Record<string, readonly unknown[]> = {},
> extends CoreLifecycle<TProps, TEvents> {}
